export enum SortDirection {
  Ascending = 1,
  Descending = -1
}

/**
 * Creates a comparator for Array.prototype.sort that orders items by a date-like field.
 *
 * The extractor `dateExtractor` is called for each item and may return:
 *   - a Date instance
 *   - a date-like string (e.g. ISO-8601)
 *   - a timestamp number
 *   - null or undefined
 *
 * A value is considered invalid if:
 *   - it is null/undefined
 *   - it cannot be parsed as a Date
 *   - or it represents a year <= 1970
 *
 * Resolution rules:
 *   - If `dateExtractor` returns a valid value, it is used.
 *   - If the value is invalid, the item is treated as having time `0` (i.e. sorted as the earliest possible date).
 *
 * Overloads:
 *   - byDate(extractor, direction?)
 *   - byDate(extractor, fallbackExtractor, direction?)
 *
 * @param dateExtractor   Extracts the date-like value from an item.
 * @param direction       Sort direction
 */
export function byDate<T>(
  dateExtractor: (item: T) => Date | string | number | null | undefined,
  direction?: SortDirection
): (a: T, b: T) => number;


/**
 * Creates a comparator for Array.prototype.sort that orders items by a primary
 * date-like field, with an optional fallback field when the primary is missing or invalid.
 *
 * The primary extractor `dateExtractor` is called for each item and may return:
 *   - a Date instance
 *   - a date-like string (e.g. ISO-8601)
 *   - a timestamp number
 *   - null or undefined
 *
 * The `fallbackDateExtractor` is used when the primary value is missing or considered invalid.
 *
 * A value is considered invalid if:
 *   - it is null/undefined
 *   - it cannot be parsed as a Date
 *   - or it represents a year <= 1970
 *
 * Resolution rules:
 *   - If `dateExtractor` returns a valid value, it is used.
 *   - Otherwise, if `fallbackDateExtractor` returns a valid value, that value is used.
 *   - If both are invalid, the item is treated as having time `0` (i.e. sorted as the earliest possible date).
 *
 * Overloads:
 *   - byDate(extractor, direction?)
 *   - byDate(extractor, fallbackExtractor, direction?)
 *
 * @param dateExtractor   Extracts the primary date-like value from an item.
 * @param fallbackDateExtractor Extracts a fallback date-like value when the primary is missing or invalid.
 * @param direction       Sort direction
 */
export function byDate<T>(
  dateExtractor: (item: T) => Date | string | number | null | undefined,
  fallbackDateExtractor: (item: T) => Date | string | number | null | undefined,
  direction?: SortDirection
): (a: T, b: T) => number;

// implementation
export function byDate<T>(
  dateExtractor: (item: T) => Date | string | number | null | undefined,
  fallbackOrDirection?: ((item: T) => Date | string | number | null | undefined) | SortDirection,
  maybeDirection: SortDirection = SortDirection.Ascending
) {
  const fallbackDateExtractor =
    typeof fallbackOrDirection === 'function' ? fallbackOrDirection : undefined;

  const direction =
    typeof fallbackOrDirection === 'number' ? fallbackOrDirection : maybeDirection;

  const toTimeOrNaN = (value: Date | string | number | null | undefined): number => {
    if (value == null) {
      return NaN;
    }

    let d: Date;
    if (value instanceof Date) {
      d = value;
    } else {
      d = new Date(value);
    }

    const time = d.getTime();
    if (isNaN(time) || d.getFullYear() <= 1970) {
      return NaN;
    }

    return time;
  };

  const resolveTime = (item: T): number => {
    const primary = toTimeOrNaN(dateExtractor(item));
    if (!isNaN(primary)) {
      return primary;
    }

    if (fallbackDateExtractor) {
      const fallback = toTimeOrNaN(fallbackDateExtractor(item));
      if (!isNaN(fallback)) {
        return fallback;
      }
    }

    // If both primary and fallback are invalid, treat as time 0
    return 0;
  };

  return (a: T, b: T) => {
    const ta = resolveTime(a);
    const tb = resolveTime(b);
    return (ta - tb) * direction;
  };
}
